import React from 'react'
import s from './styles/DataTable.module.scss'
import { SortableHeaderCell as DefaultSortableHeaderCell } from './components/SortableHeaderCell/SortableHeaderCell'

const normalizeId = v => String(v)

export const DataTable = ({
	className = '',
	shellClassName = '',
	scrollClassName = '',
	tableClassName = '',
	headClassName = '',
	bodyClassName = '',

	columns = [],
	rows = [],
	getRowId,

	onRowClick,
	isRowClickable = false,

	stickyHeader = true,

	minWidth,
	emptyText = '—',
	variant,
	layout = 'auto',

	headerCellComponent: HeaderCellCmp,

	renderExpandedRow,
	expandedRowIds = [],
	onToggleExpand,
	getRowProps,
}) => {
	const rowKey = getRowId || (row => row?.id)
	const SortHeader = HeaderCellCmp || DefaultSortableHeaderCell

	return (
		<section className={`${s.section} ${className}`.trim()}>
			<div
				className={`${s.shell} ${shellClassName} ${variant === 'light' ? s.light : s.dark
					}`.trim()}
			>
				<div className={`${s.scroll} ${scrollClassName}`.trim()}>
					<div className={s.inner}>
						<table
							className={`${s.table} ${tableClassName}`.trim()}
							style={
								minWidth
									? { minWidth, tableLayout: layout }
									: { tableLayout: layout }
							}
						>
							<thead className={`${s.head} ${headClassName}`.trim()}>
								<tr>
									{columns.map(col => {
										const stickyLeft = Number(col?.stickyLeft)
										const isStickyLeft = Number.isFinite(stickyLeft)
										const stickyRight = Number(col?.stickyRight)
										const isStickyRight = Number.isFinite(stickyRight)
										const isCorner = !!stickyHeader && (isStickyLeft || isStickyRight)

										const thClass = [
											s.headerCell,
											stickyHeader ? s.headerSticky : '',
											isStickyLeft || isStickyRight ? s.headerCellSticky : '',
											isStickyRight ? s.headerCellStickyRight : '',
											isCorner ? s.cornerCell : '',
											col?.headerClassName || '',
										]
											.filter(Boolean)
											.join(' ')

										const style = {
											...(isStickyLeft ? { left: stickyLeft } : null),
											...(isStickyRight ? { right: stickyRight } : null),
											...(col?.width
												? { width: col.width, minWidth: col.width, maxWidth: col.width }
												: null),
										}

										if (typeof col?.headerRender === 'function') {
											return (
												<th key={normalizeId(col.id)} className={thClass} style={style}>
													{col.headerRender()}
												</th>
											)
										}

										if (!col?.sortable || !col?.sortOptions?.length) {
											return (
												<th key={normalizeId(col.id)} className={thClass} style={style}>
													<span className={s.headerText}>{col.label}</span>
												</th>
											)
										}

										return (
											<th key={normalizeId(col.id)} className={thClass} style={style}>
												<SortHeader
													label={col.label}
													isActive={!!col.isSortActive}
													dir={col.sortDir}
													options={col.sortOptions}
													onSelect={opt => col.onSortSelect?.(opt, col)}
												/>
											</th>
										)
									})}
								</tr>
							</thead>

							{rows?.length ? (
								rows.map((row, idx) => {
									const id = rowKey(row)
									const isExpanded = expandedRowIds.includes(id)
									const clickable = isRowClickable || typeof onRowClick === 'function'

									const toggleExpand = (e) => {
										if (e) { e.stopPropagation(); e.preventDefault(); }
										onToggleExpand?.(id)
									}

									const rowElements = []

									if (idx === 0) {
										rowElements.push(
											<tr key={`${normalizeId(id)}_top_spacer`} className={s.rowSpacer}>
												<td colSpan={columns.length}></td>
											</tr>
										)
									}

									rowElements.push(
										<tr
											key={normalizeId(id)}
											id={`row-${id}`}
											className={`${s.row} ${clickable ? s.rowClickable : ''} ${isExpanded ? s.rowExpandedActive : ''}`.trim()}
											onClick={clickable ? () => onRowClick?.(row) : undefined}
										>
											{columns.map((col, colIdx) => {
												const stickyLeft = Number(col?.stickyLeft)
												const isStickyLeft = Number.isFinite(stickyLeft)
												const stickyRight = Number(col?.stickyRight)
												const isStickyRight = Number.isFinite(stickyRight)
												const isFirstCol = colIdx === 0
												const tdClass = [
													s.cell,
													isStickyLeft || isStickyRight ? s.cellSticky : '',
													isStickyRight ? s.cellStickyRight : '',
													col?.cellClassName || '',
												].filter(Boolean).join(' ')

												const style = {
													...(isStickyLeft ? { left: stickyLeft } : null),
													...(isStickyRight ? { right: stickyRight } : null),
													...(col?.width
														? { width: col.width, minWidth: col.width, maxWidth: col.width }
														: null),
												}

												const content = typeof col?.render === 'function'
													? col.render(row, { isExpanded, toggleExpand })
													: row?.[col.id]

												return (
													<td
														key={`${normalizeId(id)}_${normalizeId(col.id)}`}
														className={tdClass}
														style={style}
													>
														{isFirstCol && <div className={s.strokeTab} />}
														{content ?? emptyText}
													</td>
												)
											})}
										</tr>
									)

									if (renderExpandedRow && isExpanded) {
										rowElements.push(
											<tr key={`${normalizeId(id)}_expanded`} className={`${s.expandedRow} ${s.rowExpandedActive}`}>
												<td colSpan={columns.length} className={s.expandedCell}>
													{renderExpandedRow(row)}
												</td>
											</tr>
										)
									}

									rowElements.push(
										<tr key={`${normalizeId(id)}_spacer`} className={s.rowSpacer}>
											<td colSpan={columns.length}></td>
										</tr>
									)

									const customProps = typeof getRowProps === 'function' ? getRowProps(row, { isExpanded }) : {}
									const { className: customClassName = '', style: customStyle = {}, ...restProps } = customProps

									return (
										<tbody
											key={normalizeId(id)}
											className={`${s.body} ${bodyClassName} ${isExpanded ? s.expandedGroup : ''} ${s.rowGroup} ${customClassName}`.trim()}
											style={customStyle}
											{...restProps}
										>
											{rowElements}
										</tbody>
									)
								})
							) : (
								<tbody className={`${s.body} ${bodyClassName}`.trim()}>
									<tr className={s.emptyRow}>
										<td className={s.emptyCell} colSpan={columns.length}>
											{emptyText}
										</td>
									</tr>
								</tbody>
							)}
						</table>
					</div>
				</div>
			</div>
		</section>
	)
}
